import type {
  AlarmState,
  AreaState,
  BypassState,
  BypassZoneState,
  CockpitState,
  PgmItem,
  PgmState,
  SirenItem,
  SirenState,
  TroubleState,
} from "../api";
import type { Tab } from "../ui-contract";

export type R8OperationalTone =
  | "armed"
  | "disarmed"
  | "inhibited"
  | "failure"
  | "neutral"
  | "alarm"
  | "illuminated";

export type R8VisualPhase = "stable" | "pending" | "degraded" | "failure";
export type R8Availability = "available" | "unbound";
export type R8HeaderControlId = "external" | "internal" | "floodlights" | "camera-alert" | "industrial-siren";
export type R8HeaderIconId = "shield" | "shield-circle" | "floodlight" | "beacon" | "industrial-siren";

export interface R8StatePresentation {
  tone: R8OperationalTone;
  phase: R8VisualPhase;
  stateLabel: string;
  description: string;
  pressed?: boolean;
  availability: R8Availability;
}

export interface R8HeaderControlPresentation extends R8StatePresentation {
  id: R8HeaderControlId;
  icon: R8HeaderIconId;
  label: string;
}

export interface R8ProtectionSummary {
  protectedZones: number;
  totalZones: number;
}

export interface R8CockpitRiskPresentation extends R8ProtectionSummary {
  activeOutputs: number;
  tone: R8OperationalTone;
  title: string;
  summary: string;
  actionLabel: string;
  actionTab: Tab;
  noImmediateAction: boolean;
}

export function selectProtectionSummary(
  alarm: AlarmState,
  bypass: BypassState,
): R8ProtectionSummary {
  const activeBypass = new Set(bypass.active.map((item) => item.zone));
  const allZoneIds = new Set(alarm.areas.flatMap((area) => area.zones));
  const protectedZoneIds = new Set(
    alarm.areas
      .filter((area) => area.state === "ARMED")
      .flatMap((area) => area.zones)
      .filter((zone) => !activeBypass.has(zone)),
  );
  const fallbackTotal = alarm.areas.reduce((total, area) => total + area.zone_count, 0);

  return {
    protectedZones: protectedZoneIds.size,
    totalZones: allZoneIds.size || fallbackTotal,
  };
}

export function selectCockpitRiskPresentation(input: {
  alarm: AlarmState;
  cockpit: CockpitState;
  pgms: PgmState;
  siren: SirenState;
  bypass: BypassState;
  troubles: TroubleState;
}): R8CockpitRiskPresentation {
  const { alarm, cockpit, pgms, siren, bypass, troubles } = input;
  const { protectedZones, totalZones } = selectProtectionSummary(alarm, bypass);
  const activeOutputs = pgms.pgms.filter((item) => item.state === "ON").length
    + (siren.siren.state === "ON" ? 1 : 0);
  const criticalTrouble = troubles.active.find((item) => item.severity === "CRITICAL");
  const warningTrouble = troubles.active.find((item) => item.severity === "WARNING");

  let tone: R8OperationalTone = "armed";
  let title = "Proteção confirmada";
  let summary = `${protectedZones} de ${totalZones} zonas com proteção ativa e sem bypass.`;
  let actionLabel = "VER ZONAS";
  let actionTab: Tab = "SECURITY";

  if (siren.siren.state === "ON") {
    tone = "alarm";
    title = "Sirene lógica ativa";
    summary = `SIREN_MAIN v${siren.siren.siren_version} está ON${siren.siren.physical_output ? " com saída física declarada" : " sem confirmação de saída física"}.`;
    actionLabel = "ABRIR AUTOMAÇÃO";
    actionTab = "AUTOMATION";
  } else if (criticalTrouble) {
    tone = "failure";
    title = criticalTrouble.message;
    summary = `${criticalTrouble.resource_id} · ${criticalTrouble.occurrence_count} ocorrência(s) técnica(s).`;
    actionLabel = "ABRIR EVENTOS";
    actionTab = "EVENTS";
  } else if (cockpit.overall_status === "CRITICAL") {
    tone = "failure";
    title = "Intervenção necessária";
    summary = cockpit.reasons.filter(Boolean).join(" · ") || "O cockpit autoritativo sinaliza estado crítico.";
    actionLabel = "ABRIR SISTEMA";
    actionTab = "SYSTEM";
  } else if (warningTrouble) {
    tone = "failure";
    title = warningTrouble.message;
    summary = `${warningTrouble.resource_id} · ${warningTrouble.occurrence_count} ocorrência(s) técnica(s).`;
    actionLabel = "ABRIR EVENTOS";
    actionTab = "EVENTS";
  } else if (cockpit.overall_status === "DEGRADED") {
    tone = "failure";
    title = "Confiança degradada";
    summary = cockpit.reasons.filter(Boolean).join(" · ") || "O cockpit autoritativo requer verificação técnica.";
    actionLabel = "ABRIR SISTEMA";
    actionTab = "SYSTEM";
  } else if (bypass.active.length > 0) {
    tone = "inhibited";
    title = `${bypass.active.length} zona${bypass.active.length === 1 ? "" : "s"} temporariamente inibida${bypass.active.length === 1 ? "" : "s"}`;
    summary = "O bypass é lógico e não aciona relés; revise duração e motivo.";
  } else if (alarm.areas.some((area) => area.state === "DISARMED")) {
    tone = "disarmed";
    title = "Proteção parcial";
    summary = "Uma ou mais áreas estão deliberadamente desarmadas.";
  } else if (activeOutputs > 0) {
    tone = "neutral";
    title = `${activeOutputs} saída${activeOutputs === 1 ? "" : "s"} ativa${activeOutputs === 1 ? "" : "s"}`;
    summary = "O estado exibido foi relido do ImerG.";
    actionLabel = "ABRIR AUTOMAÇÃO";
    actionTab = "AUTOMATION";
  }

  return {
    protectedZones,
    totalZones,
    activeOutputs,
    tone,
    title,
    summary,
    actionLabel,
    actionTab,
    noImmediateAction: actionLabel === "VER ZONAS" && tone === "armed",
  };
}

export type R8HeaderControls = readonly [
  R8HeaderControlPresentation,
  R8HeaderControlPresentation,
  R8HeaderControlPresentation,
  R8HeaderControlPresentation,
  R8HeaderControlPresentation,
];

export type R8TrustTone = "fresh" | "aging" | "failure" | "unknown";

export interface R8TrustPresentation {
  tone: R8TrustTone;
  shortAge: string;
  stateLabel: string;
  accessibleLabel: string;
}

export type ExplicitPgmSemantics = "GENERIC_LOGICAL" | "FLOODLIGHT" | "CAMERA_ALERT";

const STABLE_TRANSITIONS = new Set(["", "NONE", "IDLE", "STABLE"]);
const HEALTHY_VALUES = new Set(["OK", "HEALTHY", "NOMINAL"]);
const FAILURE_VALUES = new Set(["ERROR", "FAILED", "FAILURE", "FAULT", "INVALID"]);

function normalized(value: string | undefined): string {
  return (value ?? "").trim().toUpperCase();
}

function phaseForArea(area: AreaState): R8VisualPhase {
  const health = normalized(area.health);
  if (FAILURE_VALUES.has(health)) return "failure";
  if (!STABLE_TRANSITIONS.has(normalized(area.transition))) return "pending";
  if (!HEALTHY_VALUES.has(health)) return "degraded";
  return "stable";
}

function unavailable(description: string): R8StatePresentation {
  return {
    tone: "neutral",
    phase: "stable",
    stateLabel: "Sem confirmação",
    description,
    availability: "unbound",
  };
}

export function selectAreaPresentation(area: AreaState | null | undefined): R8StatePresentation {
  if (!area) return unavailable("Partição não disponível no estado autoritativo.");

  const phase = phaseForArea(area);
  if (phase === "failure") {
    return {
      tone: "failure",
      phase,
      stateLabel: "Falha técnica",
      description: `${area.display_name}: saúde ${area.health || "não informada"}.`,
      availability: "available",
    };
  }

  const armed = area.state === "ARMED";
  const transition = normalized(area.transition);
  const pendingLabel = transition.includes("DISARM")
    ? "Desarmando"
    : transition.includes("ARM")
      ? "Armando"
      : "Mudança pendente";
  const stateLabel = phase === "pending" ? pendingLabel : armed ? "Armado" : "Desarmado";
  return {
    tone: armed ? "armed" : "disarmed",
    phase,
    stateLabel,
    description: phase === "pending"
      ? `${area.display_name}: ${pendingLabel.toLowerCase()}; aguardando confirmação autoritativa.`
      : `${area.display_name}: ${armed ? "proteção armada" : "proteção desarmada"} e estado ${phase === "stable" ? "confirmado" : phase}.`,
    pressed: armed,
    availability: "available",
  };
}

/**
 * A PGM only receives resource-specific color semantics when its caller supplies
 * an explicit, separately verified binding. No PGM id is mapped here.
 */
export function selectPgmPresentation(
  pgm: PgmItem | null | undefined,
  semantics: ExplicitPgmSemantics,
): R8StatePresentation {
  if (!pgm) return unavailable("PGM não disponível no estado autoritativo.");

  const active = pgm.state === "ON";
  const tone: R8OperationalTone = active
    ? semantics === "FLOODLIGHT"
      ? "illuminated"
      : semantics === "CAMERA_ALERT"
        ? "alarm"
        : "neutral"
    : "disarmed";

  return {
    tone,
    phase: "stable",
    stateLabel: active ? "Ligado" : "Desligado",
    description: `${pgm.pgm_id}: saída lógica ${active ? "ligada" : "desligada"} e versão ${pgm.pgm_version}.`,
    pressed: active,
    availability: "available",
  };
}

export function selectSirenPresentation(siren: SirenItem | null | undefined): R8StatePresentation {
  if (!siren) return unavailable("Sirene industrial não disponível no estado autoritativo.");

  const active = siren.state === "ON";
  const physicalState = siren.physical_output
    ? "saída física declarada ativa pelo contrato"
    : "sem confirmação de saída física";
  return {
    tone: active ? "alarm" : "disarmed",
    phase: "stable",
    stateLabel: active ? "Ativa (lógica)" : "Desligada",
    description: `Sirene industrial em estado lógico ${active ? "ON" : "OFF"}, versão ${siren.siren_version}; ${physicalState}.`,
    pressed: active,
    availability: "available",
  };
}

export function selectBypassPresentation(zone: BypassZoneState | null | undefined): R8StatePresentation {
  if (zone === undefined) return unavailable("Estado de bypass da zona não carregado.");

  if (zone === null) {
    return {
      tone: "disarmed",
      phase: "stable",
      stateLabel: "Sem bypass ativo",
      description: "Nenhum bypass ativo confirmado para a zona.",
      pressed: false,
      availability: "available",
    };
  }

  if (zone.status === "ACTIVE") {
    return {
      tone: "inhibited",
      phase: "stable",
      stateLabel: "Bypass ativo",
      description: `Zona ${zone.zone} temporariamente inibida na versão ${zone.bypass_version}.`,
      pressed: true,
      availability: "available",
    };
  }

  return {
    tone: zone.status === "RESTORED" ? "disarmed" : "neutral",
    phase: "stable",
    stateLabel: zone.status === "RESTORED" ? "Restaurada" : "Bypass expirado",
    description: `Zona ${zone.zone}: ${zone.status === "RESTORED" ? "proteção restaurada" : "bypass expirado"}.`,
    pressed: false,
    availability: "available",
  };
}

function unavailableHeaderControl(
  id: Extract<R8HeaderControlId, "floodlights" | "camera-alert">,
  icon: Extract<R8HeaderIconId, "floodlight" | "beacon">,
  label: string,
): R8HeaderControlPresentation {
  return {
    id,
    icon,
    label,
    ...unavailable(`${label}: vínculo funcional ainda não confirmado pelo contrato da API.`),
  };
}

export function selectOperationalHeaderControls(input: {
  alarm?: AlarmState | null;
  siren?: SirenState | null;
}): R8HeaderControls {
  const external = input.alarm?.areas.find((area) => area.area === "EXTERNAL");
  const internal = input.alarm?.areas.find((area) => area.area === "INTERNAL");

  return [
    {
      id: "external",
      icon: "shield",
      label: "Proteção externa",
      ...selectAreaPresentation(external),
    },
    {
      id: "internal",
      icon: "shield-circle",
      label: "Proteção interna",
      ...selectAreaPresentation(internal),
    },
    unavailableHeaderControl("floodlights", "floodlight", "Holofotes"),
    unavailableHeaderControl("camera-alert", "beacon", "Alerta de câmeras"),
    {
      id: "industrial-siren",
      icon: "industrial-siren",
      label: "Sirene industrial",
      ...selectSirenPresentation(input.siren?.siren),
    },
  ];
}

function trustAge(generatedAtUtc: string, nowMs: number): { shortAge: string; seconds: number | null } {
  const generatedAtMs = Date.parse(generatedAtUtc);
  if (!Number.isFinite(generatedAtMs) || generatedAtMs - nowMs > 5_000) {
    return { shortAge: "—", seconds: null };
  }

  const seconds = Math.max(0, Math.floor((nowMs - generatedAtMs) / 1_000));
  if (seconds < 60) return { shortAge: `${seconds}s`, seconds };
  if (seconds < 3_600) return { shortAge: `${Math.floor(seconds / 60)}m`, seconds };
  if (seconds < 86_400) return { shortAge: `${Math.floor(seconds / 3_600)}h`, seconds };
  return { shortAge: `${Math.floor(seconds / 86_400)}d`, seconds };
}

export function selectTrustPresentation(
  cockpit: CockpitState | null | undefined,
  nowMs: number = Date.now(),
): R8TrustPresentation {
  if (!cockpit) {
    return {
      tone: "unknown",
      shortAge: "—",
      stateLabel: "Estado desconhecido",
      accessibleLabel: "Confiança operacional desconhecida; sem leitura do cockpit.",
    };
  }

  const freshness = normalized(cockpit.freshness.status);
  const { shortAge, seconds } = trustAge(cockpit.generated_at_utc, nowMs);
  const maxAgeSeconds = Number(cockpit.freshness.max_age_seconds);
  const ageContractValid = seconds !== null && Number.isFinite(maxAgeSeconds) && maxAgeSeconds > 0;
  let tone: R8TrustTone = freshness === "FRESH"
    ? "fresh"
    : freshness === "AGING" || freshness === "STALE"
      ? "aging"
      : FAILURE_VALUES.has(freshness)
        ? "failure"
        : "unknown";
  if (tone !== "failure" && !ageContractValid) tone = "unknown";
  if (tone === "fresh" && seconds !== null && seconds > maxAgeSeconds) tone = "aging";
  const stateLabel = tone === "fresh"
    ? "Confirmado"
    : tone === "aging"
      ? "Leitura envelhecendo"
      : tone === "failure"
        ? "Falha de verificação"
        : "Estado desconhecido";

  return {
    tone,
    shortAge,
    stateLabel,
    accessibleLabel: `Confiança operacional: ${stateLabel.toLowerCase()}; idade da leitura ${shortAge}.`,
  };
}
